/**
 * features/notifications/PushEnrollButton.tsx
 * ------------------------------------------------------------------
 * Opt-in control for real-time web push. Sits next to the bell in the
 * Shell header.
 *
 * Behaviour:
 *   - On mount, if permission is already 'granted', silently refresh
 *     the FCM token (tokens rotate) so the device stays reachable.
 *   - If push is available but not yet decided ('default'), show a
 *     "Enable alerts" button that prompts and enrolls on click.
 *   - If denied / unsupported / no VAPID key configured, render nothing
 *     (the app still delivers in-app + email notifications).
 * ------------------------------------------------------------------
 */
import { useEffect, useState } from 'react';
import { BellRing, BellPlus, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { enrollPush, pushAvailable, pushPermission } from '@/lib/push';

export default function PushEnrollButton() {
  const { userData } = useAuth();
  const uid = userData?.uid;
  const [available, setAvailable] = useState(false);
  const [perm, setPerm] = useState<NotificationPermission | 'unsupported'>('default');
  const [busy, setBusy] = useState(false);
  const [justEnrolled, setJustEnrolled] = useState(false);

  // Determine availability + silently refresh the token when already granted.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await pushAvailable();
      if (cancelled) return;
      setAvailable(ok);
      setPerm(pushPermission());
      if (ok && uid && pushPermission() === 'granted') {
        // Silent refresh — no prompt.
        void enrollPush(uid, false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  if (!available || !uid) return null;
  if (perm === 'denied' || perm === 'unsupported') return null;
  // Already granted → the silent refresh handles it; no button needed.
  if (perm === 'granted') return null;

  async function handleClick() {
    if (!uid) return;
    setBusy(true);
    const res = await enrollPush(uid, true);
    setBusy(false);
    setPerm(pushPermission());
    if (res === 'enrolled') {
      setJustEnrolled(true);
      setTimeout(() => setJustEnrolled(false), 2500);
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      title="Enable real-time alerts on this device"
      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
    >
      {justEnrolled ? <Check size={15} /> : busy ? <BellRing size={15} className="animate-pulse" /> : <BellPlus size={15} />}
      <span className="hidden sm:inline">{justEnrolled ? 'Alerts on' : 'Enable alerts'}</span>
    </button>
  );
}
